import { useAutoAnimate } from "@formkit/auto-animate/react";
import {
  useMemberAsMemberNew,
  useMemberInvoiceAsMember,
  useMutationPayInvoiceAsMember,
} from "@gymflow/api";
import { cn, formatCurrency } from "@gymflow/helpers";
import { useMutation } from "@tanstack/react-query";
import { usePortalRoutes, useRedirectUrl } from "apps/portal/src/hooks";
import useScaModal from "apps/portal/src/hooks/useScaModal";
import { useClubSettings } from "apps/portal/src/providers";
import { ToastContext } from "apps/portal/src/providers/ToastProvider/context";
import useGymflowModels from "apps/portal/src/store";
import { apiV2 } from "libs/api/src/lib/apiV2";
import { DateTime } from "luxon";
import qs from "qs";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { Button } from "../../atoms";
import { PaymentMethodPicker } from "../../molecules/PaymentMethodPicker";
import { HostedPagesBackHeader } from "../components/HostedPagesBackHeader";

export const HostedPagesGiftVoucherCheckout = () => {
  const [invoiceId, setInvoiceId] = useState<string>();
  const [paymentMethodId, setPaymentMethodId] = useState<string>();
  const [parent] = useAutoAnimate();
  const [orderKey] = useState<string>(`${uuidv4()}`);

  const { isEmbed } = usePortalRoutes();
  const { toast } = useContext(ToastContext);
  const showScaModal = useScaModal({
    asMember: true,
  });

  const {
    club: { defaultCurrency },
  } = useClubSettings();
  const { t } = useTranslation();
  const location = useLocation();
  const { redirectToCustomizedWebsiteOr } = useRedirectUrl();

  const { api } = useGymflowModels();
  const { data: userMember } = useMemberAsMemberNew({ api });
  const { data: invoice } = useMemberInvoiceAsMember({
    api,
    invoiceNumber: invoiceId,
  });

  const { mutateAsync: checkoutGiftVoucher } = useMutation(
    apiV2.member.voucher.mutations.order(),
  );

  const { mutateAsync: payFailed } = useMutationPayInvoiceAsMember({
    api,
  });

  const isInvoiceSuccessful =
    invoice && (invoice.status === "PAID" || invoice.status === "PROCESSING");

  useEffect(() => {
    if (isInvoiceSuccessful) {
      toast({
        message: t("page.hostedPagesVoucherCheckout.successfulMessage.title"),
        description: t(
          "page.hostedPagesVoucherCheckout.successfulMessage.description",
          { email: userMember?.email },
        ),
        intent: "success",
      });
      setTimeout(() => {
        redirectToCustomizedWebsiteOr();
      }, 1500);
    }
  }, [
    isInvoiceSuccessful,
    t,
    toast,
    userMember?.email,
    redirectToCustomizedWebsiteOr,
  ]);

  const query = qs.parse(location.search, { ignoreQueryPrefix: true });
  const amountParam = query["amount"];

  const amount =
    typeof amountParam === "string" ? parseFloat(amountParam) : null;

  if (amount === null || isNaN(amount) || amount < 0.5) {
    return (
      <div className="dark:text-gray-0 flex w-full justify-center p-4">
        {t("page.hostedPagesVoucherCheckout.invalidVoucher")}
      </div>
    );
  }

  const handleCheckout = async () => {
    if (!paymentMethodId) return;
    const result = await checkoutGiftVoucher({
      orderKey,
      paymentMethodId,
      amount: String(amount),
    });

    if (result.invoiceNumber) {
      setInvoiceId(result.invoiceNumber);
      await showScaModal({
        invoiceNumber: result.invoiceNumber,
      });
    }
  };

  return (
    <div
      className={cn(
        "track-height flex w-full flex-col items-center overflow-y-auto",
        { "h-full": !isEmbed, "h-fit": isEmbed },
      )}
    >
      <HostedPagesBackHeader label="Checkout" />
      <div
        ref={parent}
        className="flex h-fit w-full max-w-lg flex-col gap-4 px-4 py-6"
      >
        {invoice && (
          <div className="flex flex-col items-center gap-2">
            <div className="text-3xl font-bold text-gray-950">
              {formatCurrency(invoice.totalAmount, defaultCurrency)}
            </div>
            {isInvoiceSuccessful && (
              <div className="flex flex-col items-center">
                <div className="text-success-600 text-center font-bold">
                  {t("pages.hostedPagesCheckout.paymentSuccessful")}
                </div>
                <div className="font-medium text-gray-600 dark:text-gray-400">
                  {DateTime.fromISO(
                    invoice.issueDate ?? new Date().toISOString(),
                  ).toLocaleString(DateTime.DATE_MED)}
                </div>
              </div>
            )}
            {invoice.status === "PAST_DUE" && (
              <div className="flex flex-col items-center gap-3">
                <div className="text-error-600 dark:text-error-400 text-center font-bold">
                  {t("pages.hostedPagesCheckout.paymentFailed")}
                </div>
                <div className="font-medium text-gray-600 dark:text-gray-400">
                  <Button
                    onClick={async () => {
                      const newInvoice = await payFailed({
                        invoiceNumber: invoice.number,
                        paymentMethodId: paymentMethodId,
                      });
                      showScaModal({
                        invoiceNumber: newInvoice.number,
                      });
                    }}
                  >
                    {t("pages.hostedPagesCheckout.retry")}
                  </Button>
                </div>
              </div>
            )}
            {invoice.status === "AWAITING_AUTHORIZATION" && (
              <div className="text-warning-600 text-center font-bold">
                {t("pages.hostedPagesCheckout.authorize")}
              </div>
            )}
          </div>
        )}
        <div className="dark:text-gray-0 text-base font-semibold">
          {t("page.hostedPagesVoucherCheckout.takePayment")}
        </div>
        {userMember && (
          <PaymentMethodPicker
            userMemberId={userMember.id}
            currentPaymentMethodId={paymentMethodId}
            onChange={setPaymentMethodId}
            disabled={!!invoice}
            allowBankPayment
            asMember
          />
        )}
        <div className="flex w-full border-b-2 border-dashed border-gray-400" />
        <div className="flex flex-col gap-2">
          <div className="flex w-full flex-row justify-between">
            <div className="font-medium text-gray-500">
              {t("page.hostedPagesVoucherCheckout.name")}
            </div>
            <div className="font-medium text-gray-500">
              {formatCurrency(amount, defaultCurrency)}
            </div>
          </div>

          <div className="dark:text-gray-0 flex w-full flex-row items-center justify-between text-gray-950">
            <div className="text-xl font-bold">
              {t("pages.hostedPagesCheckout.amountDue")}
            </div>
            <div className="text-xl font-bold">
              {formatCurrency(amount, defaultCurrency)}
            </div>
          </div>
        </div>
        {!invoice && (
          <Button
            intent="secondary"
            className="w-full"
            disabled={false}
            onClick={handleCheckout}
          >
            {t("page.hostedPagesVoucherCheckout.pay", {
              amount: formatCurrency(amount, defaultCurrency),
            })}
          </Button>
        )}
      </div>
    </div>
  );
};
export default HostedPagesGiftVoucherCheckout;
